import React from 'react';

import { gql, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Collapse, Container, FormControl, FormLabel, Grid, Stack, TextField, Typography } from '@mui/material';
import { get } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { BottomSafeArea, LoadingButton, useGoBack, useTrackPageView } from '../../../components';
import { AppContext } from '../../../contexts/app_context';
import { createScopedI18n } from '../../../i18n/i18n';
import { joinPairs } from '../../../utils/libs';
import { getChainUsersGql } from '../page';

const usersNewPageI18n = createScopedI18n('pages.users_new');
const usersNewErrorI18n = createScopedI18n('graphql.errors.types.create_user_on_chain.fields', { joinOutput: true });

const createUserOnChainGql = gql`
  mutation createUserOnChain($chainId: ID!, $phoneNumber: String!, $email: String!) {
    createUserOnChain(chainId: $chainId, phoneNumber: $phoneNumber, email: $email) {
      success
      errors
      data {
        id
        username
        email
      }
    }
  }
`;

const createUserOnChainSchema = Yup.object({
  phoneNumber: Yup.string()
    .required(usersNewErrorI18n('phone_number.blank'))
    .matches(/^\d+$/, usersNewErrorI18n('phone_number.invalid'))
    .min(9, usersNewErrorI18n('phone_number.too_short'))
    .max(10, usersNewErrorI18n('phone_number.too_long')),
  email: Yup.string().required(usersNewErrorI18n('email.blank')).email(usersNewErrorI18n('email.invalid')),
});

type CreateUserOnChainValueType = Yup.InferType<typeof createUserOnChainSchema>;

type CreatedUser = {
  id: string;
  username?: string | null;
  email?: string | null;
};

type ChainUsersData = {
  user: {
    id: string;
    chain?: {
      id: string;
      users?: CreatedUser[] | null;
    } | null;
  };
};

const UsersNewPage = () => {
  useTrackPageView('UsersNewPage');

  const navigate = useNavigate();
  const goBack = useGoBack({ fallbackPath: '/users' });
  const { contextUser } = React.useContext(AppContext);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(createUserOnChainSchema),
  });

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [createUserOnChainMutation] = useMutation(createUserOnChainGql);

  const submitHandler = async (value: CreateUserOnChainValueType) => {
    setErrorMessage(null);

    const chainId = contextUser?.chain?.id;
    if (!chainId) {
      setErrorMessage(usersNewPageI18n('chain_missing'));
      return;
    }

    try {
      const { data } = await createUserOnChainMutation({
        variables: {
          chainId,
          phoneNumber: value.phoneNumber,
          email: value.email,
        },
        update: (cache, { data: mutationData }) => {
          const newUser = mutationData?.createUserOnChain?.data;
          if (!newUser) return;

          cache.updateQuery<ChainUsersData>({ query: getChainUsersGql }, (prev) => {
            if (!prev?.user?.chain?.users) return prev;
            return {
              user: {
                ...prev.user,
                chain: {
                  ...prev.user.chain,
                  users: [...prev.user.chain.users, newUser],
                },
              },
            };
          });
        },
      });

      if (data?.createUserOnChain?.success) {
        const newUserId = data.createUserOnChain.data?.id;
        if (newUserId) {
          navigate(`/users/${newUserId}`, { replace: true });
        } else {
          goBack();
        }
      } else {
        setErrorMessage(joinPairs(data?.createUserOnChain?.errors));
      }
    } catch (error) {
      setErrorMessage(get(error, 'message', null));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ paddingY: 2 }}>
      <Typography variant="h6" sx={{ paddingBottom: 2 }}>
        {usersNewPageI18n('title')}
      </Typography>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Grid container gap={2}>
          <Controller
            key="phoneNumber"
            name="phoneNumber"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <FormLabel>{usersNewPageI18n('phone_number')} *</FormLabel>
                <TextField
                  id={field.name}
                  type="tel"
                  fullWidth
                  autoComplete="tel"
                  value={value ?? ''}
                  error={invalid}
                  helperText={error?.message}
                  {...field}
                />
              </FormControl>
            )}
          />

          <Controller
            key="email"
            name="email"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <FormLabel>{usersNewPageI18n('email')} *</FormLabel>
                <TextField
                  id={field.name}
                  type="email"
                  fullWidth
                  autoComplete="email"
                  value={value ?? ''}
                  error={invalid}
                  helperText={error?.message}
                  {...field}
                />
              </FormControl>
            )}
          />

          <Collapse in={!!errorMessage} sx={{ width: '100%' }}>
            <Alert severity="error">{errorMessage}</Alert>
          </Collapse>

          <Stack flexDirection="row" gap={1} sx={{ width: '100%' }}>
            <LoadingButton type="submit" disabled={isSubmitting} loading={isSubmitting} fullWidth variant="contained">
              {usersNewPageI18n('submit')}
            </LoadingButton>
          </Stack>
        </Grid>
      </form>

      <BottomSafeArea />
    </Container>
  );
};

export default UsersNewPage;
